import { useContext, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import { Button, ButtonProps } from '@chakra-ui/react';
import { getIn, useFormikContext } from 'formik';
import { IoSparkles } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { Deal, estimateUpsert } from '../../gql/dealGql';
import {
  LienholderRequirement,
  useAcquirePayoffForDealMutation,
  useCheckSupportedLienholderQuery,
} from '../../gql/generated/graphql';

import Tooltip from '../shared/Tooltip';

import { PermissionEnum } from '../../constants/permissions';
import { Can } from '../../libs/Can';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import { cleanDealForEstimateUpsert } from '../../utils/deals';

type FetchPayoffButtonProps = ButtonProps;

const ssnSatisfiedMap: Partial<Record<LienholderRequirement, string[]>> = {
  [LienholderRequirement.LastFourSsn]: [
    'customer.has_last_4_ssn',
    'customer.has_last_6_ssn',
    'customer.has_full_ssn',
  ],
  [LienholderRequirement.LastSixSsn]: ['customer.has_last_6_ssn', 'customer.has_full_ssn'],
  [LienholderRequirement.Ssn]: ['customer.has_full_ssn'],
};

const fieldPathMap: Partial<Record<LienholderRequirement, string>> = {
  [LienholderRequirement.Mileage]: 'car.mileage',
  [LienholderRequirement.Dob]: 'customer.dob',
  [LienholderRequirement.DobYear]: 'customer.dob',
  [LienholderRequirement.FirstName]: 'customer.first_name',
  [LienholderRequirement.LastName]: 'customer.last_name',
  [LienholderRequirement.Make]: 'car.make',
  [LienholderRequirement.Model]: 'car.model',
  [LienholderRequirement.PhoneNumber1]: 'customer.phone_number',
  [LienholderRequirement.AddressLine]: 'customer.address.address_line',
  [LienholderRequirement.City]: 'customer.address.city',
  [LienholderRequirement.State]: 'customer.address.state',
  [LienholderRequirement.Vin]: 'car.vin',
  [LienholderRequirement.Year]: 'car.year',
  [LienholderRequirement.Zip]: 'customer.address.zip',
};

const requirementLabelMap: Record<LienholderRequirement, string> = {
  [LienholderRequirement.Mileage]: 'Mileage',
  [LienholderRequirement.Dob]: 'Date of Birth',
  [LienholderRequirement.DobYear]: 'Date of Birth',
  [LienholderRequirement.FirstName]: 'First Name',
  [LienholderRequirement.LastName]: 'Last Name',
  [LienholderRequirement.LastFourSsn]: 'Last 4 of SSN',
  [LienholderRequirement.LastSixSsn]: 'Last 6 of SSN',
  [LienholderRequirement.Ssn]: 'Full SSN',
  [LienholderRequirement.Make]: 'Make',
  [LienholderRequirement.Model]: 'Model',
  [LienholderRequirement.PhoneNumber1]: 'Phone Number',
  [LienholderRequirement.AddressLine]: 'Address Line',
  [LienholderRequirement.City]: 'City',
  [LienholderRequirement.State]: 'State',
  [LienholderRequirement.Vin]: 'VIN',
  [LienholderRequirement.Year]: 'Year',
  [LienholderRequirement.Zip]: 'Zip',
};
const FetchPayoffButton = ({ ...rest }: FetchPayoffButtonProps) => {
  const { deal, dispatch, isPayoffRequested, payoffRequestRefetch } = useContext(DealContext);
  const { values } = useFormikContext<Deal>();

  const dealId = Number(deal.id) ?? undefined;
  const lienholderSlug = values.car?.payoff?.lienholder_slug;

  const [upsertEstimate, { loading: loadingUpsertEstimate }] = useMutation(estimateUpsert);
  const [acquirePayoff, { loading: loadingAcquirePayoff }] = useAcquirePayoffForDealMutation();
  const { data: supportData, loading: loadingSupport } = useCheckSupportedLienholderQuery({
    skip: !dealId,
    variables: { dealId, lienholderSlug },
    fetchPolicy: 'network-only',
    onError: (error) => {
      toast.error(error.message);
      logger.error('FetchPayoffButton.tsx', 'Failed to check supported lienholder', null, error);
    },
  });

  const isSupported = supportData?.checkSupportedLienholder?.isSupported ?? false;
  const requiredFields = supportData?.checkSupportedLienholder?.requiredFields ?? [];

  // Check which required fields are missing based on current Formik values
  const actuallyMissingFields = useMemo(() => {
    if (!requiredFields || requiredFields.length === 0) {
      return [];
    }

    return requiredFields.filter((field) => {
      const ssnPaths = ssnSatisfiedMap[field];
      if (ssnPaths) {
        return !ssnPaths.some((path) => getIn(values, path) === true);
      }

      const valuePath = fieldPathMap[field];
      if (!valuePath) {
        return true;
      }

      const value = getIn(values, valuePath);
      return value == null || value === '';
    });
  }, [requiredFields, values]);

  // Build tooltip errors based on actual missing fields
  const tooltipErrors = useMemo(() => {
    const errors = [];
    if (!isSupported && !loadingSupport) {
      errors.push('Lienholder not supported for AI payoff');
    }
    if (isSupported && actuallyMissingFields.length > 0) {
      const labels = Array.from(new Set(actuallyMissingFields.map((f) => requirementLabelMap[f])));
      errors.push(`Missing: ${labels.join(', ')}`);
    }
    return errors;
  }, [isSupported, loadingSupport, actuallyMissingFields]);

  // Update requirementsMet based on actual form values
  const actualRequirementsMet = actuallyMissingFields.length === 0;

  const isDisabled =
    loadingSupport ||
    loadingUpsertEstimate ||
    !isSupported ||
    !actualRequirementsMet ||
    isPayoffRequested;

  const handleFetchPayoff = async () => {
    if (isDisabled) {
      return;
    }

    try {
      // Save deal changes before acquiring payoff
      if (values.id) {
        const updatedDeal = cleanDealForEstimateUpsert(values);
        const res = await upsertEstimate({
          variables: { deal: updatedDeal },
        });

        // Update DealContext with saved data
        dispatch({ type: DealActionsEnum.UpdateDeal, payload: res.data.estimateUpsert });
      }

      // Then acquire payoff
      const { data } = await acquirePayoff({
        variables: {
          dealId,
          payloadFields: requiredFields,
        },
      });

      if (data?.acquirePayoffForDeal?.received) {
        toast.success('AI payoff fetch initiated. Processing...');
        // Refetch to get the new payoff request
        await payoffRequestRefetch();
      } else {
        toast.error('Failed to initiate AI payoff fetch');
      }
    } catch (error) {
      toast.error('Failed to fetch payoff via AI');
      logger.error('FetchPayoffButton.tsx', 'Failed to fetch payoff', null, error);
    }
  };

  // Don't render if lienholder info is missing
  if (!dealId || !lienholderSlug) {
    return null;
  }

  return (
    <Can I={PermissionEnum.RequestPayoff}>
      <Tooltip errors={tooltipErrors}>
        <Button
          {...rest}
          variant="aiAction"
          leftIcon={<IoSparkles />}
          isDisabled={isDisabled}
          isLoading={loadingAcquirePayoff || loadingUpsertEstimate}
          loadingText="FETCH PAYOFF"
          onClick={handleFetchPayoff}
        >
          FETCH PAYOFF
        </Button>
      </Tooltip>
    </Can>
  );
};

export default FetchPayoffButton;
